import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Actions } from '@ngrx/effects';
import { Observable } from 'rxjs';
import { ManagementBaseActions } from './management-base.actions';
import { ManagementBaseSelectors } from './management-base.selectors';
import {AsyncActionQueries, AsyncActionSelectors} from '@emm/core/util/async-action-state';
import { Attribute } from '@emm/core/models/attribute.model';
import {Item} from '@emm/core/models/item.model';
import {ItemLocale, Locale} from '@emm/core/models/locale.model';
import {ItemType} from '@emm/core/models/item-type.model';
import {LocationSet} from '@emm/core/models/location-set.model';
import {LocationSetType} from '@emm/core/models/location-set-type.model';
import {Revotes} from '@emm/core/models/revotes.model';
import {SearchFilter} from '@emm/core/models/search-filter';
import {AsyncActionStoreService} from '@emm/core/store/async-action-store.service';
import {getActionQueries} from '@emm/core/util/ngrx.util';
import { State as CoreState } from '@emm/core/store/core.reducer';
import {Screen} from '@emm/core/models/screen.model';


export interface ManagementBaseQueries extends AsyncActionQueries {
    attributeDetail$: Observable<Attribute>;
    emergencyItemDetail$: Observable<{
        tag: string,
        type: string,
        subType: string,
        statusMessage: string,
        active: boolean,
    }>;
    initialized$: Observable<boolean>;
    itemDetail$: Observable<Item>;
    itemLocaleDetail$: Observable<ItemLocale>;
    itemGroupingDetail$: Observable<Item>;
    itemTypeDetail$: Observable<ItemType>;
    locationSetDetail$: Observable<LocationSet>;
    locationSetTypeDetail$: Observable<LocationSetType>;
    revoteDetail$: Observable<Revotes>;
    screens$: Observable<Screen[]>;
    selectedCountry$: Observable<Locale>;
    selectedScreen$: Observable<Screen>;
    all$: Observable<any[]>;
    locations$: Observable<any[]>;
    searchFilter$: Observable<SearchFilter>;
}

@Injectable({ providedIn: 'root' })
export class ManagementBaseStoreService extends AsyncActionStoreService<AsyncActionSelectors> {

    constructor(store: Store<CoreState>, private actions$: Actions) {
        super(store, ManagementBaseSelectors);
    }

    dispatch = {
        changeScreen: (screen: Screen, navigate: boolean = true) =>
            this.store.dispatch(ManagementBaseActions.changeScreen({ screen, navigate })),
        init: () =>
            this.store.dispatch(ManagementBaseActions.init()),
        initAttributes: () =>
            this.store.dispatch(ManagementBaseActions.initAttributes()),
        initConsumerTypes: () =>
            this.store.dispatch(ManagementBaseActions.initConsumerTypes()),
        initItemGroupings: () =>
            this.store.dispatch(ManagementBaseActions.initItemGroupings()),
        initItemTypes: () =>
            this.store.dispatch(ManagementBaseActions.initItemTypes()),
        initItems: () =>
            this.store.dispatch(ManagementBaseActions.initItems()),
        initLocationSets: () =>
            this.store.dispatch(ManagementBaseActions.initLocationSets()),
        initLocationSetTypes: () =>
            this.store.dispatch(ManagementBaseActions.initLocationSetTypes()),
        initOperatorEmergency: () =>
            this.store.dispatch(ManagementBaseActions.initOperatorEmergency()),
        initMasterMenuSystem: () =>
            this.store.dispatch(ManagementBaseActions.initMasterMenuSystem()),
        initMmsAdmin: () =>
            this.store.dispatch(ManagementBaseActions.initMmsAdmin()),
        initLanguageAssignments: () =>
            this.store.dispatch(ManagementBaseActions.initLanguageAssignments()),
        loadItemLocale: (tag: string, country: string, language: string) =>
            this.store.dispatch(ManagementBaseActions.loadItemLocaleDetail({ tag, country, language })),
        selectCountry: (country: Locale) =>
            this.store.dispatch(ManagementBaseActions.selectCountry({ country })),
        selectEmergencyItemDetail: (item: { tag: string }) =>
            this.store.dispatch(ManagementBaseActions.selectEmergencyItemDetail({ item })),
        selectLocationSet: (locationSet: LocationSet) =>
            this.store.dispatch(ManagementBaseActions.selectLocationSet({ locationSet })),
        updateItem: (tag: string, updates: any, dispatchReload: boolean) =>
            this.store.dispatch(ManagementBaseActions.updateItem({ tag, updates, dispatchReload })),
        updateItemType: (tag: string, updates: any, dispatchReload: boolean) =>
            this.store.dispatch(ManagementBaseActions.updateItemType({ tag, updates, dispatchReload })),
        updateLocationSetType: (tag: string, updates: any, dispatchReload: boolean) =>
            this.store.dispatch(ManagementBaseActions.updateLocationSetType({ tag, updates, dispatchReload })),
        updateItemGrouping: (tag: string, updates: any, dispatchReload: boolean) =>
            this.store.dispatch(ManagementBaseActions.updateItemGrouping({ tag, updates, dispatchReload })),
        updateLocationSet: (tag: string, updates: any, dispatchReload: boolean) =>
            this.store.dispatch(ManagementBaseActions.updateLocationSet({ tag, updates, dispatchReload })),
        updateRevote: (id: string, updates: any, dispatchReload: boolean) =>
            this.store.dispatch(ManagementBaseActions.updateRevote({ id, updates, dispatchReload })),
        searchFilter: (location: any) => this.store.dispatch(ManagementBaseActions.searchLocation(location)),
        searchLocation: (filter: SearchFilter) =>
            this.store.dispatch(ManagementBaseActions.searchLocation({ filter })),
        startLoading: () => this.store.dispatch(ManagementBaseActions.startLoading()),
        stopLoading: () => this.store.dispatch(ManagementBaseActions.loadingSuccess()),
    };

    queries: ManagementBaseQueries = {
        attributeDetail$: this.select(ManagementBaseSelectors.getAttributeDetail),
        emergencyItemDetail$: this.select(ManagementBaseSelectors.getEmergencyItemDetail),
        initialized$: this.select(ManagementBaseSelectors.getInitialized),
        itemDetail$: this.select(ManagementBaseSelectors.getItemDetail),
        itemGroupingDetail$: this.select(ManagementBaseSelectors.getItemGroupingDetail),
        itemLocaleDetail$: this.select(ManagementBaseSelectors.getItemLocaleDetail),
        itemTypeDetail$: this.select(ManagementBaseSelectors.getItemTypeDetail),
        locationSetDetail$: this.select(ManagementBaseSelectors.getLocationSetDetail),
        locationSetTypeDetail$: this.select(ManagementBaseSelectors.getLocationSetTypeDetail),
        revoteDetail$: this.select(ManagementBaseSelectors.getRevoteDetails),
        screens$: this.select(ManagementBaseSelectors.getScreens),
        selectedCountry$: this.selectNonNil(ManagementBaseSelectors.getSelectedCountry),
        selectedScreen$: this.selectNonNil(ManagementBaseSelectors.getSelectedScreen),
        searchFilter$: this.selectNonNil(ManagementBaseSelectors.getSearchFilter),
        all$: this.selectNonNil(ManagementBaseSelectors.getFilteredItems),
        locations$: this.selectNonNil(ManagementBaseSelectors.getLocations),
        ...this.asyncActionQueries,
        ...getActionQueries({
            actionCreators: ManagementBaseActions,
            actions$: this.actions$
        })
    };
}
